import * as Config from '@/Config';
import Store from '@/Store';

/**
 * Service for tracking app version history and checking version-based conditions.
 * Used for "what's new" dialogs, feature prompts, and version-gated re-prompting.
 *
 * @example Initialize in App.vue mounted()
 *
 *     Service.VersionHistory.initialize();
 *
 * @example Show "What's New" dialog after upgrade
 *
 *     if (Service.VersionHistory.didJustUpgrade()) {
 *         this.showWhatsNewDialog();
 *     }
 *
 * @example Re-prompt users who haven't seen a feature since version 3.27.0
 *
 *     if (!Service.VersionHistory.hasSeenSince('3.27.0')) {
 *         this.showFeaturePrompt();
 *     }
 *
 * @example Check if app version meets a requirement for feature flags
 *
 *     if (Service.VersionHistory.meetsVersion('>=3.2.0')) {
 *         this.enableNewFeature();
 *     }
 *
 * @example Welcome new users on first launch
 *
 *     if (Service.VersionHistory.isFirstLaunchEver()) {
 *         this.showOnboardingTour();
 *     }
 *
 * @example Show version-specific content on first visit to that version
 *
 *     if (Service.VersionHistory.isFirstTimeInVersion()) {
 *         this.showVersionNotes();
 *     }
 *
 * @author Matt Kenefick <matt@polymermallard.com>
 * @class VersionHistory
 * @package App/Service
 * @project ChalkySticks App
 */
export class VersionHistory {
	/**
	 * Initialize version tracking. Call this in App.vue mounted().
	 *
	 * @return void
	 */
	public static initialize(): void {
		Store.dispatch('versionHistory/initialize');
	}

	/**
	 * Compare two semver versions.
	 * Returns -1 if a < b, 0 if equal, 1 if a > b
	 *
	 * @param string a
	 * @param string b
	 * @return number
	 */
	public static compareVersions(a: string, b: string): number {
		const partsA = a.split('.').map(Number);
		const partsB = b.split('.').map(Number);
		const maxLength = Math.max(partsA.length, partsB.length);

		for (let i = 0; i < maxLength; i++) {
			const numA = partsA[i] || 0;
			const numB = partsB[i] || 0;

			if (numA < numB) return -1;
			if (numA > numB) return 1;
		}

		return 0;
	}

	/**
	 * Check if user just upgraded from a previous version.
	 *
	 * @return boolean
	 */
	public static didJustUpgrade(): boolean {
		const lastSeen = this.getPreviousVersion();
		return lastSeen !== null && lastSeen !== Config.App.VERSION;
	}

	/**
	 * Get the current app version.
	 *
	 * @return string
	 */
	public static getCurrentVersion(): string {
		return Config.App.VERSION;
	}

	/**
	 * Get the previous version the user was on (before current).
	 *
	 * @return string
	 */
	public static getPreviousVersion(): string {
		return Store.getters['versionHistory/lastSeenVersion'] || Config.App.VERSION_HISTORY_START_VERSION || '1.0.0';
	}

	/**
	 * Get all versions the user has seen.
	 *
	 * @return string[]
	 */
	public static getSeenVersions(): string[] {
		return Store.getters['versionHistory/seenVersions'];
	}

	/**
	 * Check if user has ever seen any version >= the specified version.
	 * Useful for: "re-prompt users who haven't seen 3.27.0 or higher"
	 *
	 * @param string version
	 * @return boolean
	 */
	public static hasSeenSince(version: string): boolean {
		const seenVersions = this.getSeenVersions();

		return seenVersions.some((seen) => this.compareVersions(seen, version) >= 0);
	}

	/**
	 * Check if user has seen a specific version.
	 *
	 * @param string version
	 * @return boolean
	 */
	public static hasSeenVersion(version: string): boolean {
		return this.getSeenVersions().includes(version);
	}

	/**
	 * Check if this is a fresh install (no version history).
	 *
	 * @return boolean
	 */
	public static isFirstLaunchEver(): boolean {
		return Store.getters['versionHistory/isFirstLaunchEver'];
	}

	/**
	 * Check if user is seeing the current (or specified) version for the first time.
	 *
	 * @param string version Optional, defaults to current version
	 * @return boolean
	 */
	public static isFirstTimeInVersion(version?: string): boolean {
		const targetVersion = version || Config.App.VERSION;
		const seenVersions = this.getSeenVersions();

		// If version is in seen list but is the last one added (current session), it's first time
		if (version) {
			return !seenVersions.includes(targetVersion);
		}

		// For current version, check if we just upgraded
		return this.didJustUpgrade() || this.isFirstLaunchEver();
	}

	/**
	 * Check if the current app version meets a version requirement.
	 * Supports operators: >, >=, <, <=, =, ==
	 *
	 * @example
	 *   meetsVersion('>=3.2.0')  // true if current version is 3.2.0 or higher
	 *   meetsVersion('>3.2')    // true if current version is greater than 3.2.0
	 *   meetsVersion('<=4.0.0') // true if current version is 4.0.0 or lower
	 *   meetsVersion('=3.27.4') // true if current version is exactly 3.27.4
	 *   meetsVersion('3.2.0')   // defaults to >=, true if 3.2.0 or higher
	 *
	 * @param string requirement Version requirement with optional operator
	 * @return boolean
	 */
	public static meetsVersion(requirement: string): boolean {
		const currentVersion = this.getCurrentVersion();
		const match = requirement.match(/^(>=|<=|>|<|={1,2})?(.+)$/);

		if (!match) {
			return false;
		}

		const operator = match[1] || '>=';
		const targetVersion = match[2].trim();
		const comparison = this.compareVersions(currentVersion, targetVersion);

		switch (operator) {
			case '>':
				return comparison > 0;

			case '>=':
				return comparison >= 0;

			case '<':
				return comparison < 0;

			case '<=':
				return comparison <= 0;

			case '=':
			case '==':
				return comparison === 0;

			default:
				return comparison >= 0;
		}
	}
}

export default VersionHistory;
